import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import moment from 'moment';
import { Box, Grid, IconButton } from '@mui/material';
import { RemoveCircleOutlined as DeleteIcon } from '@mui/icons-material';

import { FHAntdDateTimePicker, FHInputField } from '../../../base';
import { validateSchema, validateSync } from '../../../../helpers/validation-helpers';
import { MedicationsRowSchema } from './validationSchema';
import { MEDICATIONS_ERROR_STATE_PREFIX } from './medicationsConstants';
import { Themes } from '../../../../constants';
import { medicationsActions } from '../../../../store';
import { getUtcOffset } from '../../../../utils/common';
import { FORTA_BLUE } from '../../../../utils/colors';

import type { IMedication } from '../../../../utils/types';
import type { Moment } from 'moment';

interface MedicationsRowProps {
  medication: IMedication;
  planId?: number;
  editable?: boolean;
  index: number;
}

function MedicationRow({ medication, planId, editable, index }: MedicationsRowProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const { creatingMedication } = useSelector((state: any) => state.medications);

  const [errors, setErrors] = useState<any>({});

  const { id: medicationId } = medication;

  const listOfKeys = ['medication_name', 'dosage', 'date_start', 'physician', 'reason'];

  let isEditable = editable;

  if (!medicationId) {
    isEditable = editable && !creatingMedication;
  }

  useEffect(() => {
    if (!medication || Object.keys(medication).length <= 1) {
      return;
    }
    listOfKeys.forEach((key: string) => {
      handleValidate(key, medication);
    });
  }, [medication]);

  useEffect(() => {
    const errorCount = Object.values(errors).filter((value) => value).length;
    dispatch(
      medicationsActions.updateErrorState({
        [`${MEDICATIONS_ERROR_STATE_PREFIX}-${medication?.id ? medicationId : 'new-' + index}`]:
          errorCount,
      }),
    );
  }, [errors]);

  useEffect(() => {
    return () => {
      dispatch(
        medicationsActions.updateErrorState({
          [`${MEDICATIONS_ERROR_STATE_PREFIX}-${medication?.id ? medicationId : 'new-' + index}`]:
            0,
        }),
      );
    };
  }, []);

  const handleFieldUpdate = (field: string, value: string) => {
    const updatedData = { ...medication, [field]: value };
    dispatch(
      medicationsActions.updateUnsavedMedication({
        index,
        data: updatedData,
      }),
    );
  };

  const handleValidate = (key: string, data: IMedication = medication) => {
    if (!data) return;

    const isValid = validateSchema(MedicationsRowSchema, key, data);
    setErrors((prev: any) => ({ ...prev, [key]: !isValid }));
  };

  const handleSave = async () => {
    const data: IMedication = medication?.date_start
      ? {
          ...medication,
          date_start: medication?.date_start,
          utc_offset: getUtcOffset(moment(new Date())),
        }
      : {
          ...medication,
          utc_offset: getUtcOffset(moment(new Date())),
        };
    const isValid = validateSync(MedicationsRowSchema, data);
    if (!isValid || !planId) return;

    if (!medicationId) {
      dispatch(
        medicationsActions.createMedication({ data, getAccessTokenSilently, planId, index }),
      );
    } else {
      dispatch(
        medicationsActions.updateMedication({ getAccessTokenSilently, planId, medicationId, data }),
      );
    }
  };

  const handleBlur = (key: string) => {
    handleValidate(key);
    handleSave();
  };

  const handleDeleteMedication = async () => {
    if (!medicationId) {
      dispatch(medicationsActions.removeUnsavedMedication({ index }));
      return;
    }
    dispatch(medicationsActions.deleteMedication({ getAccessTokenSilently, planId, medicationId }));
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }} mb={2}>
      {editable && (
        <Box width={35}>
          <IconButton sx={{ p: 0, m: 0 }} disabled={!isEditable} onClick={handleDeleteMedication}>
            <DeleteIcon data-testid="deleteMedication" sx={{ color: FORTA_BLUE.primary }} />
          </IconButton>
        </Box>
      )}
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <FHInputField
            data-testid="medicationNameTextField"
            value={medication?.medication_name ?? ''}
            fullWidth
            disabled={!isEditable}
            colorTheme={Themes.Primary}
            onChange={(e) => handleFieldUpdate('medication_name', e.target.value)}
            onBlur={() => handleBlur('medication_name')}
            inputHeight={40}
            error={errors['medication_name']}
          />
        </Grid>
        <Grid item xs={1}>
          <FHInputField
            data-testid="dosageTextField"
            value={medication?.dosage ?? ''}
            fullWidth
            disabled={!isEditable}
            colorTheme={Themes.Primary}
            sx={{ minWidth: 50 }}
            onChange={(e) => handleFieldUpdate('dosage', e.target.value)}
            onBlur={() => handleBlur('dosage')}
            inputHeight={40}
            error={errors['dosage']}
          />
        </Grid>
        <Grid item xs={1.5}>
          <FHAntdDateTimePicker
            data-testid="medicationStarteDatePicker"
            value={medication?.date_start ? moment(medication?.date_start) : null}
            format="MM/YY"
            allowClear={false}
            use12Hours
            style={{ width: '100%' }}
            colorTheme={Themes.Primary}
            disabled={!isEditable}
            picker={'month'}
            onChange={(e: Moment) => handleFieldUpdate('date_start', e?.date(1)?.toISOString())}
            onBlur={() => handleBlur('date_start')}
            inputHeight={40}
            error={errors['date_start']}
          />
        </Grid>
        <Grid item xs={3}>
          <FHInputField
            data-testid="medicationPhysicianTextField"
            value={medication?.physician ?? ''}
            fullWidth
            disabled={!isEditable}
            colorTheme={Themes.Primary}
            onChange={(e) => handleFieldUpdate('physician', e.target.value)}
            onBlur={() => handleBlur('physician')}
            inputHeight={40}
            error={errors['physician']}
          />
        </Grid>
        <Grid item xs={3.5}>
          <FHInputField
            data-testid="medicationReasonTextField"
            value={medication?.reason ?? ''}
            fullWidth
            disabled={!isEditable}
            colorTheme={Themes.Primary}
            onChange={(e) => handleFieldUpdate('reason', e.target.value)}
            onBlur={() => handleBlur('reason')}
            inputHeight={40}
            error={errors['reason']}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

export default MedicationRow;
